import React from 'react'
import { ArrowRight, Building2, Check, CreditCard } from 'lucide-react'
import { cn } from '@/lib/utils'

const card = 'rounded-2xl border border-border bg-card p-5 shadow-sm sm:p-6'

const Points: React.FC<{ items: string[] }> = ({ items }) => (
  <ul className="space-y-2">
    {items.map((p) => (
      <li key={p} className="flex gap-2.5 text-sm text-foreground">
        <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#16A34A]" strokeWidth={2.5} />
        {p}
      </li>
    ))}
  </ul>
)

const IconTile: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#F1F5F0] text-foreground">
    {children}
  </span>
)

/** Pay as deals are agreed: the default for most brands */
export const StandardFundingCard: React.FC<{
  recommended?: boolean
  compact?: boolean
  points?: string[]
  children?: React.ReactNode
}> = ({ recommended, compact, points, children }) => (
  <section className={cn(card, 'flex flex-col')}>
    <div className={cn('flex items-start justify-between gap-3', compact && 'items-center')}>
      <div className={cn(compact && 'flex items-center gap-3')}>
        <IconTile>
          <CreditCard className="h-6 w-6" />
        </IconTile>
        {compact ? (
          <div>
            <h2 className="flex flex-wrap items-center gap-x-2 gap-y-1 text-lg font-bold text-foreground">
              Standard funding
              {recommended ? (
                <span className="rounded-full bg-[#E8F9D2] px-2.5 py-0.5 text-xs font-semibold text-[#2F5E06]">
                  Recommended
                </span>
              ) : null}
            </h2>
            <p className="text-sm text-muted-foreground">
              Best for most marketers and local businesses.
            </p>
          </div>
        ) : null}
      </div>
      {recommended && !compact ? (
        <span className="rounded-full bg-[#E8F9D2] px-3 py-1 text-xs font-semibold text-[#2F5E06]">
          Recommended
        </span>
      ) : null}
    </div>
    {!compact ? (
      <>
        <h2 className="mt-5 text-2xl font-bold text-foreground">Standard funding</h2>
        <p className="mt-1 text-muted-foreground">Best for most marketers and local businesses.</p>
      </>
    ) : null}
    <div className="mt-4">
      <Points
        items={
          points ?? [
            'No setup required during onboarding',
            'Fund accepted deals before paid work starts',
            'Charges are securely processed as campaign activity happens',
          ]
        }
      />
    </div>
    {children ? <div className="mt-5">{children}</div> : null}
  </section>
)

/** Invoice billing with a spending limit, for approved organizations */
export const CompanyCreditCard: React.FC<{ compact?: boolean; children?: React.ReactNode }> = ({
  compact,
  children,
}) => (
  <section className={cn(card, 'flex flex-col')}>
    <div className={cn(compact && 'flex items-center gap-3')}>
      <IconTile>
        <Building2 className="h-6 w-6" />
      </IconTile>
      {compact ? (
        <div>
          <h2 className="text-lg font-bold text-foreground">Apply for company credit</h2>
          <p className="text-sm text-muted-foreground">
            For approved organizations that prefer invoice billing.
          </p>
        </div>
      ) : null}
    </div>
    {!compact ? (
      <>
        <h2 className="mt-5 text-2xl font-bold text-foreground">Apply for company credit</h2>
        <p className="mt-1 text-muted-foreground">
          For approved organizations that prefer invoice billing.
        </p>
      </>
    ) : null}
    <div className="mt-4">
      <Points
        items={[
          'Reviewed by the Athios team',
          'Approved spending limit and payment terms',
          'Best for larger brands and agencies',
        ]}
      />
    </div>
    {children ? <div className="mt-5">{children}</div> : null}
  </section>
)

/** Deal accepted → secure funding → athlete work begins */
export const PaymentFlowSteps: React.FC = () => (
  <ol className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)_auto_minmax(0,1fr)] lg:items-stretch">
    {[
      ['Deal accepted', 'You and an athlete agree on the terms and deliverables.'],
      ['Secure funding', 'Athios prompts you to fund the deal before paid work starts.'],
      [
        'Athlete work begins',
        'The athlete starts creating content and delivering on the agreement.',
      ],
    ].map(([title, body], i) => (
      <React.Fragment key={title}>
        <li className="flex min-w-0 gap-3 rounded-xl border border-border p-4">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#C0FD72] text-sm font-bold text-[#0F172A]">
            {i + 1}
          </span>
          <span className="min-w-0">
            <span className="block font-semibold text-foreground">{title}</span>
            <span className="block text-sm text-muted-foreground">{body}</span>
          </span>
        </li>
        {i < 2 ? (
          <ArrowRight
            className="hidden h-5 w-5 self-center text-muted-foreground lg:block"
            aria-hidden
          />
        ) : null}
      </React.Fragment>
    ))}
  </ol>
)
